import {
  Group,
  InputProps,
  Stack,
  StackProps,
  Text,
  Textarea,
  TextProps,
} from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton, IBasicButtonProps } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { useAsyncAction } from 'src/design/molecules/useAsyncAction';
import { characterCount } from 'src/utils';

export interface IInlineTextInputProps
  extends Omit<InputProps, 'onChange' | 'value'> {
  initialValue: string;
  onSave: (value: string) => void | Promise<void>;
  emptyText?: string;

  /** Caps the editor and shows a live "used / max" counter under it. */
  maxLength?: number;

  /** Puts the text and an icon-only Edit control on a single row. */
  inline?: boolean;

  containerProps?: StackProps;
  textProps?: TextProps;
  saveButtonProps?: Omit<IBasicButtonProps, 'onClick' | 'loading'>;
  cancelButtonProps?: Omit<IBasicButtonProps, 'onClick'>;
  editButtonProps?: Omit<IBasicButtonProps, 'onClick'>;
}

/** @registry Click-to-edit text display: shows plain text with an Edit button, swaps to a Textarea with Save/Cancel while editing; onSave only fires on explicit save. Pass `inline` to put the text and an icon-only Edit control on a single row, `maxLength` to cap the editor and show a character counter. */
export const InlineTextInput: React.FC<IInlineTextInputProps> = ({
  initialValue,
  onSave,
  emptyText = 'Add a value',
  maxLength,
  inline = false,
  containerProps,
  textProps,
  saveButtonProps,
  cancelButtonProps,
  editButtonProps,
  ...rest
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const save = useAsyncAction(onSave);

  // Kept out of the effect deps so save/cancel (which flip editing back to
  // false) don't re-trigger this sync and revert `value` to a stale prop
  // before the parent's own update lands.
  const editingRef = useRef(editing);
  editingRef.current = editing;

  useEffect(() => {
    // Don't let a prop refresh (e.g. react-query refetch) clobber unsaved
    // in-progress edits.
    if (!editingRef.current) setValue(initialValue);
  }, [initialValue]);

  const handleSave = async () => {
    if (await save.run(value)) setEditing(false);
  };

  const handleCancel = () => {
    setValue(initialValue);
    setEditing(false);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const handleEditClick = () => {
    setEditing(true);
  };

  const renderText = (!!value && value) || emptyText;

  return (
    <Stack gap="xs" {...containerProps}>
      {editing ? (
        <>
          <Textarea
            {...rest}
            w="100%"
            maxLength={maxLength}
            description={
              maxLength ? characterCount(value, maxLength) : undefined
            }
            value={value}
            onChange={handleChange}
            autosize
            minRows={1}
          />
          <Group justify="flex-end" gap="xs">
            <BasicButton
              variant="subtle"
              size="compact-xs"
              {...saveButtonProps}
              loading={save.loading}
              onClick={handleSave}
            >
              Save
            </BasicButton>
            <SecondaryButton
              size="compact-xs"
              {...cancelButtonProps}
              disabled={cancelButtonProps?.disabled || save.loading}
              onClick={handleCancel}
            >
              Cancel
            </SecondaryButton>
          </Group>
        </>
      ) : inline ? (
        <Group wrap="nowrap" gap="xs">
          <Text flex={1} truncate {...textProps}>
            {renderText}
          </Text>
          <BasicActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            label="Edit"
            onClick={handleEditClick}
          >
            <IconPencil size={14} />
          </BasicActionIcon>
        </Group>
      ) : (
        <>
          <Text {...textProps}>{renderText}</Text>
          <Group justify="flex-end" gap="xs">
            <BasicButton
              variant="subtle"
              size="compact-xs"
              {...editButtonProps}
              onClick={handleEditClick}
            >
              Edit
            </BasicButton>
          </Group>
        </>
      )}
    </Stack>
  );
};
